import React, { useState, useEffect, useRef, useContext } from "react";
import socket from "../../socket";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import EditIcon from "@mui/icons-material/Edit";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdKeyboardArrowRight,
} from "react-icons/md";
import { TiPinOutline } from "react-icons/ti";
import { TiPin } from "react-icons/ti";
import { PiColumnsPlusRight } from "react-icons/pi";
import { GrHide } from "react-icons/gr";
import { IoMdClose } from "react-icons/io";
import DatePicker from "react-datepicker";
import "../../styles/projects/Projects.css";
import { MultiSelect } from "react-multi-select-component";
import { useAuth } from "../Authentication/AuthContext";
import { useRolePermissions } from "../Settings/RoleContext";
import { FaChevronDown } from "react-icons/fa";
import api from "../../api/axiosConfig";
import { ProjectContext } from "../../App";
import { Helmet } from "react-helmet";
import Papa from "papaparse";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import ExcelJS from "exceljs";
import { motion } from "framer-motion";
import WorkingProgressSheet from "./WorkingProgressSheet";

const ProjectList = () => {
  const tableRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const isResizing = useRef(false);
  const fetchDataRef = useRef(null);
  const bulkUpdateRef = useRef(null);
  const projectRef = useRef(null);
  const { isNavBarOpen } = useContext(ProjectContext);
  const { userDetails } = useAuth();
  const { hasPermission } = useRolePermissions();
  const userID = userDetails.id;
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    projectno: "",
    start_date: "",
    end_date: "",
    actual_hours: "",
    budget_hours: "",
    customer_id: "",
    manager_id: [],
    contact_id: [],
    description: "",
    team_id: "",
    tags: "",
    priority: "",
  });
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [managers, setManagers] = useState([]);
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [bulkUpdate, setBulkUpdate] = useState(false);

  useEffect(() => {
    socket.on("connect", () => {
      console.log("Connected to WebSocket server");
    });

    socket.on("projectupdated", (data) => {
      setProjects((prevProjects) => {
        const updatedProjects = prevProjects.map((proj) =>
          proj.id === Number(data.projectID)
            ? { ...proj, [data.field]: data.formattedValue }
            : proj
        );

        return updatedProjects;
      });
    });
    {
      (userDetails.role_name === "Administrator" ||
        userDetails.role_name === "Super Admin") &&
        socket.on("project added", ({ newProject }) => {
          setProjects((prevProjects) => [
            ...prevProjects, // Spread previous projects
            newProject, // Add the actual project
          ]);
        });
    }
    socket.on("project users added", (userIds, projectResult) => {
      // Ensure userIds is always an array and convert to numbers
      const numericUserIds = Array.isArray(userIds)
        ? userIds.map(Number)
        : [Number(userIds)];

      // Check if userDetails.id exists in the numeric array
      if (numericUserIds.includes(userDetails.id)) {
        setProjects((prevProjects) => {
          // Ensure no duplicate projects are added
          const newProjects = projectResult.filter(
            (newProject) =>
              !prevProjects.some((proj) => proj.id === newProject.id)
          );

          return [...prevProjects, ...newProjects]; // ✅ Spread to merge the array correctly
        });
      }
    });
    socket.on("project users removed", (userIds, projectResult) => {
      // Ensure userIds is always an array and convert to numbers
      const numericUserIds = Array.isArray(userIds)
        ? userIds.map(Number)
        : [Number(userIds)];

      // Check if userDetails.id exists in the numeric array
      if (numericUserIds.includes(userDetails.id)) {
        setProjects((prevProjects) => {
          // Remove projects that match the ones in projectResult
          return prevProjects.filter(
            (proj) =>
              !projectResult.some((removedProj) => removedProj.id === proj.id)
          );
        });
      }
    });

    socket.on("project updated", ({ projectId, updatedFields }) => {
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === Number(projectId)
            ? { ...project, ...updatedFields } // Merge updated fields
            : project
        )
      );
    });

    socket.on("projectUpdated", ({ projectID, field, formattedValue }) => {
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          Number(project.id) === Number(projectID) // Ensure types match
            ? { ...project, [field]: formattedValue } // Update dynamically
            : project
        )
      );
    });

    socket.on("project deleted", (data) => {
      // Extract the `ids` array from the object
      const idsArray = Array.isArray(data.ids) ? data.ids : [data.ids];

      setProjects((prevProjects) =>
        prevProjects.filter((project) => !idsArray.includes(project.id))
      );
    });

    socket.on("project details updated", (updatedProject) => {
      const fetchNewData = async () => {
        try {
          // Fetch both project datasets in parallel
          let projectDataRes, timesheetDataRes;

          if (
            userDetails.role_name === "Administrator" ||
            userDetails.role_name === "Super Admin"
          ) {
            [projectDataRes, timesheetDataRes] = await Promise.all([
              api.get("/api/projectlistdata"),
              api.get("/api/projectlist/timesheets"),
            ]);
          } else {
            [projectDataRes, timesheetDataRes] = await Promise.all([
              api.get(`/api/projectlistdata/${userID}`),
              api.get(`/api/projectlist/timesheets/${userID}`),
            ]);
          }

          let projectData = projectDataRes.data;
          let timesheetData = timesheetDataRes.data;

          // Find the newly edited project in the updated project list
          const newlyEditedProject = projectData.find(
            (ph) => ph.id === Number(updatedProject.id)
          );

          if (!newlyEditedProject) return; // Ensure the project exists

          // Find matching timesheet data for the newly edited project
          const matchingTimesheet = timesheetData.find(
            (timesheet) => timesheet.project_id === newlyEditedProject.id
          );

          // Merge project data with its corresponding timesheet data
          const updatedProjectData = {
            ...newlyEditedProject,
            ...matchingTimesheet, // Merge timesheet data if found
          };

          // Update the state with the newly edited project data
          setProjects((prev) =>
            prev.map((p) =>
              p.id === Number(updatedProjectData.id) ? updatedProjectData : p
            )
          );
        } catch (error) {
          setError(error);
          console.error("Error fetching project data:", error.message || error);
        }
      };
      if (updatedProject) {
        fetchNewData();
      }
    });

    socket.on("newcustomer added", (newCustomer) => {
      setCustomers((prev) => [...prev, newCustomer]);
    });

    return () => {
      socket.off("projectUpdated");
      socket.off("project updated");
      socket.off("project added");
      socket.off("project deleted");
      socket.off("project users added");
      socket.off("project users removed");
      socket.off("newcustomer added");
      socket.off("project details updated");
    };
  }, []);

  useEffect(() => {
    if (userDetails) {
      setSelectedMembers((prev) => {
        // Check if the user is already in the list
        const isAlreadyAdded = prev.some(
          (member) => member.value === userDetails.id
        );
        if (isAlreadyAdded) {
          return prev; // Return the current state without changes
        }
        // Add the new user
        return [...prev, { value: userDetails.id, label: userDetails.name }];
      });
    }
  }, [userDetails]); // Only run when userDetails changes

  useEffect(() => {
    if (Array.isArray(newProject.manager_id) && users.length > 0) {
      const newMembers = newProject.manager_id
        .map((selected) => {
          const user = users.find((u) => u.id === Number(selected.value));
          return user ? { value: user.id, label: user.name } : null;
        })
        .filter(Boolean); // remove nulls if any user not found

      setSelectedMembers((prev) => {
        const existingIds = new Set(prev.map((m) => m.value));
        const merged = [...prev];

        newMembers.forEach((member) => {
          if (!existingIds.has(member.value)) {
            merged.push(member);
          }
        });

        return merged;
      });
    }
  }, [newProject.manager_id, users]);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [updatedProject, setUpdatedProject] = useState(null);
  const [updatedProjectManagers, setUpdatedProjectManagers] = useState([]);
  const [updatedProjectContacts, setUpdatedProjectContacts] = useState([]);
  const [projectGroup, setProjectGroup] = useState([]);
  const [updatedProjectGroup, setUpdatedProjectGroup] = useState([]);
  const [projectToBulkUpdate, setProjectToBulkUpdate] = useState([]);
  const [filter, setFilter] = useState("");
  const [groupBy, setGroupBy] = useState(
    localStorage.getItem("projectgroupby") || "All Projects"
  );
  useEffect(() => {
    localStorage.setItem("projectgroupby", groupBy);
  }, [groupBy]);
  const [fetchData, setFetchData] = useState(false);
  const tableHeading = [
    "Project No",
    "Project Name",
    "Hour Rate",
    "Customer Name",
    "Contact Name",
    "Team",
    "Phases",
    "VO(UC)",
    "VO PHrs(UC)",
    "VO PHrs",
    "VO AHrs",
    "Planned Hrs",
    "Actual Hrs",
    "Difference",
    "DE PHrs",
    "DE AHrs",
    "DO PHrs",
    "DO AHrs",
    "Manager",
    "Start Date",
    "Due Date",
    "INV %",
  ];
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const hasWorkinProgressAccess =
    userDetails.role_name === "Administrator" ||
    hasPermission("workinprogress", "read") ||
    userDetails.role_name === "Super Admin";
  const [activeProjLink, setProjActiveLink] = useState(() => {
    const stored = localStorage.getItem("activeproject");
    return stored
      ? stored
      : hasWorkinProgressAccess
      ? "workinprogress"
      : "activeprojects";
  });
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const handleProjLinkClick = (link) => {
    setProjectToBulkUpdate([]);
    setProjActiveLink(link); // Update the active link
    localStorage.setItem("activeproject", link);
  };
  const sampleCSV = "/SampleProjectFile/projects.csv";
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage, setProjectsPerPage] = useState(() => {
    return Number(localStorage.getItem("projectsPerPage")) || 25;
  });

  useEffect(() => {
    localStorage.setItem("projectsPerPage", projectsPerPage.toString());
  }, [projectsPerPage]);

  const calculatePaginationCount = (projectsPerPage, currentPage) => {
    const start = (currentPage - 1) * projectsPerPage + 1;
    const end = start + projectsPerPage - 1;
    return `${start}-${end}`;
  };
  const paginationCount = calculatePaginationCount(
    projectsPerPage,
    currentPage
  );

  const [visibleColumns, setVisibleColumns] = useState({
    "Project No": true,
    "Project Name": true,
    "Hour Rate": true,
    "Customer Name": true,
    "Contact Name": true,
    Team: true,
    Phases: true,
    "VO(UC)": true,
    "VO PHrs(UC)": true,
    "VO PHrs": true,
    "VO AHrs": true,
    "Planned Hrs": true,
    "Actual Hrs": true,
    Difference: true,
    "DE PHrs": true,
    "DE AHrs": true,
    "DO PHrs": true,
    "DO AHrs": true,
    Manager: true,
    "Start Date": true,
    "Due Date": true,
    "INV %": true,
  });
  const [stickyColumns, setStickyColumns] = useState(() => {
    const storedColumns = localStorage.getItem("projectstickyColumns");
    return storedColumns ? JSON.parse(storedColumns) : ["Project No"];
  });

  useEffect(() => {
    // Store the stickyColumns in localStorage whenever it updates
    localStorage.setItem("projectstickyColumns", JSON.stringify(stickyColumns));
  }, [stickyColumns]);

  const toggleStickyColumn = (heading) => {
    setStickyColumns((prev) => {
      if (prev.length === 1 && prev.includes(heading)) return prev;

      const updatedColumns = prev.includes(heading)
        ? prev.filter((col) => col !== heading) // Remove from sticky
        : [...prev, heading]; // Add to sticky

      return updatedColumns;
    });
  };
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    column: null,
  });
  const [columns, setColumns] = useState(tableHeading);
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);

  const updateColumnPositions = (stickyCols = stickyColumns) => {
    if (!tableRef.current) return;

    const table = tableRef.current;
    const thead = table.querySelector("thead");

    if (thead) {
      thead.style.position = "sticky";
      thead.style.top = "-1px";
      thead.style.zIndex = "5";
      thead.style.backgroundColor = "white";
      thead.style.borderBottom = "1px solid #9d9d9d";
    }

    const headers = table.querySelectorAll("th");
    const rows = table.querySelectorAll("tr");

    let leftOffset = 0; // Start position for sticky columns
    const lastColumnIndex = headers.length - 2; // Exclude last column (assuming it has no header)

    headers.forEach((header, index) => {
      const colName = header.innerText.trim();

      if (
        (stickyCols.includes(colName) || index === 0) &&
        index < lastColumnIndex
      ) {
        // ✅ Sticky columns (excluding last column)
        header.style.position = "sticky";
        header.style.left = `${leftOffset}px`;
        header.style.zIndex = "4";
        header.style.backgroundColor = "white";

        leftOffset += header.offsetWidth; // Move to next column position
      } else if (index < lastColumnIndex) {
        // ✅ Reset styles when unpinned (excluding last column)
        header.style.position = "";
        header.style.left = "";
        header.style.zIndex = "";
        header.style.backgroundColor = "";
        header.style.borderBottom = "";
        header.style.borderTop = "";
      }
    });

    rows.forEach((row) => {
      const cells = row.querySelectorAll("td");

      leftOffset = 0; // Reset for row

      cells.forEach((cell, index) => {
        const colName = headers[index]?.innerText.trim();

        if (
          (stickyCols.includes(colName) || index === 0) &&
          index <= lastColumnIndex
        ) {
          // ✅ Sticky columns (except last functional column)
          cell.style.position = "sticky";
          cell.style.left = `${leftOffset}px`;
          cell.style.zIndex = "2";
          cell.style.backgroundColor = "inherit";
          cell.style.borderBottom = "none";
          cell.style.borderTop = "1px solid #9d9d9d";
          leftOffset += cell.offsetWidth; // Align next sticky column
        } else {
          // ✅ Reset styles when unpinned
          cell.style.position = "";
          cell.style.left = "";
          cell.style.zIndex = "";
          cell.style.backgroundColor = "";
          cell.style.borderBottom = "";
          cell.style.borderTop = "";
        }
      });
    });
  };

  useEffect(() => {
    setTimeout(() => {
      updateColumnPositions(stickyColumns);
    }, 1000);
  }, [
    stickyColumns,
    userDetails,
    activeProjLink,
    groupBy,
    isNavBarOpen,
    !isNavBarOpen,
    filter,
    currentPage,
  ]);

  // Function to save user preferences to the backend
  const saveUserPreferences = async (preferences) => {
    const userId = userID; // Replace with the actual logged-in user's ID

    try {
      const response = await api.post("/api/save-column-preferences", {
        userId,
        preferences,
      });
    } catch (error) {
      console.error("Error saving preferences:", error);
    }
  };

  const fetchUserPreferences = async (userID) => {
    try {
      const response = await api.get(`/api/get-column-preferences/${userID}`);
      return response.data; // Axios automatically parses JSON responses
    } catch (error) {
      console.error("Error fetching preferences:", error);
      return null;
    }
  };

  const [isFormVisible, setIsFormVisible] = useState(false);
  const toggleFormVisibility = () => {
    setIsFormVisible((prevState) => !prevState);
    updateColumnPositions(stickyColumns);
  };
  //function for column visibility
  const toggleColumnVisibility = (columnKey) => {
    const updatedVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey],
    };

    // Update the state with the new visibility
    setVisibleColumns(updatedVisibleColumns);

    // Call the API to save the updated preferences
    saveUserPreferences(updatedVisibleColumns);
  };
  const handleRightClick = (event, column) => {
    event.preventDefault();
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      column: column,
    });
  };

  const hideColumn = async () => {
    if (contextMenu.column) {
      const updatedColumns = {
        ...visibleColumns,
        [contextMenu.column]: false,
      };
      setVisibleColumns(updatedColumns);
      setContextMenu({ ...contextMenu, visible: false });
      saveUserPreferences(updatedColumns);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the context menu
      if (projectRef.current && !projectRef.current.contains(event.target)) {
        setContextMenu({ ...contextMenu, visible: false });
      }
      if (
        bulkUpdateRef.current &&
        !bulkUpdateRef.current.contains(event.target)
      ) {
        setBulkUpdate(false);
      }
      if (
        fetchDataRef.current &&
        !fetchDataRef.current.contains(event.target)
      ) {
        setFetchData(false);
      }
    };

    // Attach event listener to the document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu]);

  const handleDragStart = (index) => {
    setDraggedColumnIndex(index);
  };

  const handleDrop = (dropIndex) => {
    if (draggedColumnIndex !== null) {
      // Clone columns array and reorder it
      const updatedColumns = [...columns];
      const [draggedColumn] = updatedColumns.splice(draggedColumnIndex, 1);
      updatedColumns.splice(dropIndex, 0, draggedColumn);
      //Update the columns in state
      setColumns(updatedColumns);
      // Update visibility order to match the new column order
      const newVisibleColumns = {};
      updatedColumns.forEach((col) => {
        newVisibleColumns[col] = visibleColumns[col];
      });
      setVisibleColumns(newVisibleColumns);
      // Save preferences to persist updated order and visibility
      saveUserPreferences(newVisibleColumns);
      // Reset draggedColumnIndex
      setDraggedColumnIndex(null);
    }
  };

  //saved preferences
  useEffect(() => {
    // Fetch the user preferences when the component mounts
    const loadPreferences = async () => {
      const preferences = await fetchUserPreferences(userID);
      if (
        preferences &&
        Object.keys(visibleColumns).length === Object.keys(preferences).length
      ) {
        setVisibleColumns(preferences);
        setColumns(Object.keys(preferences));
      }
    };

    loadPreferences();
  }, [userID]);

  const [columnWidths, setColumnWidths] = useState(
    Array(26).fill(150) // assuming 9 columns in your table
  );

  const startX = useRef(null);
  const startWidth = useRef(null);
  const activeColumnIndex = useRef(null);

  const handleMouseDown = (index, e) => {
    isResizing.current = true;
    startX.current = e.pageX;
    startWidth.current = columnWidths[index];
    activeColumnIndex.current = index;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (activeColumnIndex.current !== null) {
      let newWidth = startWidth.current + (e.pageX - startX.current);
      newWidth = Math.max(50, Math.min(newWidth, 500)); // Use reasonable limits
      setColumnWidths((prevWidths) => {
        const updatedWidths = [...prevWidths];
        updatedWidths[activeColumnIndex.current] = newWidth;
        return updatedWidths;
      });
    }
  };

  const handleMouseUp = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    activeColumnIndex.current = null;
  };

  //update project
  const handleEditClick = (project) => {
    setSelectedProject(project);
    setUpdatedProject({ ...project });
    setUpdatedProjectManagers(
      project?.project_managers.map((m) => ({
        label: m.label,
        value: m.value,
      }))
    );
    setUpdatedProjectContacts(
      project?.project_contacts.map((c) => ({
        label: c.label,
        value: c.value,
      }))
    );
    setUpdatedProjectGroup(
      project?.project_groups.map((g) => ({
        label: g.label,
        value: g.value,
      }))
    );
    setOpenEditDialog(true);
  };

  useEffect(() => {
    const fetchContacts = async () => {
      const customerId =
        selectedProject?.customer_id || newProject?.customer_id;

      if (customerId) {
        try {
          const { data } = await api.get(
            `/api/customers/${customerId}/contacts`
          );
          setContacts(data.contacts);
        } catch (error) {
          console.error("Error fetching contacts:", error);
          setContacts([]);
        }
      }
    };

    fetchContacts();
  }, [selectedProject?.customer_id, newProject?.customer_id]);

  //close delete dial box
  const handleCloseDial = () => {
    setIsCheckboxChecked(false);
    setDeleteDialogOpen(false);
    document.body.classList.remove("no-scroll");
  };
  //add project event handling
  const onHandleAddProject = () => {
    setOpenDialog(true);
    document.body.classList.add("no-scroll");
  };
  //new project dial
  const closeDialog = () => {
    setOpenDialog(false);
    setNewProject({
      name: "",
      projectno: "",
      start_date: "",
      end_date: "",
      actual_hours: "",
      budget_hours: "",
      manager_id: [],
      description: "",
      customer_id: "",
      team_id: "",
      tags: "",
    });
    setSelectedMembers([]);
    document.body.classList.remove("no-scroll");
  };

  //add new project
  const handleAddNewProject = async () => {
    setLoading(true);
    const formatDateForMySQL = (date, daysToAdd = 0) => {
      if (!date) return null; // If no date is provided, return null

      // Convert to Date object if it's a valid date string
      let validDate = new Date(date);
      if (isNaN(validDate)) {
        return null; // Return null if invalid date
      }

      // Add the specified number of days (30 in this case)
      validDate.setDate(validDate.getDate() + daysToAdd);

      // Adjust for timezone and return as 'YYYY-MM-DD'
      const localDate = new Date(
        validDate.getTime() - validDate.getTimezoneOffset() * 60000
      );
      return localDate.toISOString().split("T")[0]; // Convert to 'YYYY-MM-DD'
    };

    let formattedProject;
    if (newProject.start_date && newProject.end_date) {
      formattedProject = {
        ...newProject,
        start_date: formatDateForMySQL(newProject.start_date),
        end_date: formatDateForMySQL(newProject.end_date),
      };
    } else {
      formattedProject = {
        ...newProject,
        start_date: formatDateForMySQL(newProject.start_date),
        end_date: formatDateForMySQL(newProject.start_date, 30), // Add 30 days to the start_date
      };
    }
    if (
      !formattedProject.projectno ||
      !formattedProject.name ||
      !formattedProject.start_date ||
      !formattedProject.customer_id ||
      !formattedProject.manager_id
    ) {
      setLoading(false);
      toast.error("Check the input fields", {
        position: "bottom-center",
        autoClose: 2000,
      });
    } else {
      try {
        const response = await api.post(`/api/projects`, formattedProject);
        if (response.status === 201) {
          const newProject = response.data; // Newly created project
          const currentProjectId = newProject.id; // Get project ID
          // After project is added, associate selected members with the new project
          if (selectedMembers.length > 0) {
            const userIds = selectedMembers.map((member) => member.value); // Extract 'value' instead of 'id'
            await handleAssociateUsersToProject(currentProjectId, userIds);
          }

          setNewProject({
            name: "",
            projectno: "",
            start_date: "",
            end_date: "",
            actual_hours: "",
            budget_hours: "",
            manager_id: [],
            contact_id: [],
            description: "",
            customer_id: "",
            team_id: "",
            tags: "",
            priority: "",
          });
          setSelectedCustomerName("");
          closeDialog();
        }
      } catch (error) {
        console.error("Error adding project:", error);
        setLoading(false);
        // Handle specific backend errors
        if (error.response) {
          setTimeout(() => {
            if (error.response.status === 400 && error.response.data.error) {
              toast.error(error.response.data.error, {
                position: "bottom-center",
                autoClose: 2000,
              });
            } else {
              toast.error("Something went wrong. Please try again.", {
                position: "bottom-center",
                autoClose: 2000,
              });
            }
          }, 100); // Small delay to allow UI to update
        } else {
          setTimeout(() => {
            toast.error("Network error. Check your connection and try again.", {
              position: "bottom-center",
              autoClose: 2000,
            });
          }, 100);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  //add users to an project
  const handleAssociateUsersToProject = async (currentProjectId, userIds) => {
    setLoading(true);
    if (userIds.length === 0 || userIds.includes(undefined)) {
      toast.error("Error: Invalid user IDs", userIds, {
        position: "bottom-center",
        autoClose: 2000,
      });
      toast.error("Error: Invalid user selection", {
        position: "bottom-center",
        autoClose: 2000,
      });
      return;
    }
    try {
      const response = await api.post(
        `/api/projects/${currentProjectId}/users`,
        {
          userIds: userIds,
        }
      );
      const data = response.data;
      toast.error("Users associated with the project successfully.", {
        position: "bottom-center",
        autoClose: 2000,
      });

      setSelectedMembers([]);
    } catch (error) {
      console.error(
        "Error associating users with project:",
        error.response?.data || error.message
      );
      toast.error("Error associating users with project. Please try again.", {
        position: "bottom-center",
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  //edit dial box
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target; // `name` will be "managerId", `value` will be the selected manager's id
    setUpdatedProject((prev) => ({
      ...prev,
      [name]: value, // This will dynamically update the corresponding field in the updatedProject state
    }));
  };
  const handleManagerChange = (e) => {
    const { name, value } = e.target; // `name` will be "managerId", `value` will be the selected manager's id
    setUpdatedProject((prev) => ({
      ...prev,
      [name]: e, // This will dynamically update the corresponding field in the updatedProject state
    }));
  };
  const handleDateChange = (date, field) => {
    setUpdatedProject((prev) => ({
      ...prev,
      [field]: date.toISOString().split("T")[0],
    }));
  };

  const handleClickOutside = (event) => {
    // Check if the click is outside the dropdown and profile
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) //&&
      // profileRef.current &&  !profileRef.current.contains(event.target)
    ) {
      setShowDropdown(false); // Close the dropdown
    }
  };

  // Add event listener for clicks outside the component
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    if (newProject.customer_id) {
      const customer = customers.find(
        (c) => c.id === Number(newProject.customer_id)
      );
      const name = `${customer.customerno}-${customer.name}`;
      setSelectedCustomerName(name);
    }
  }, [newProject, customers]);
  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(e.target.value);
    setSelectedCustomerName("");

    const filtered = customers.filter((customer) => {
      const name = customer.name || ""; // Fallback to an empty string
      const no = customer.customerno || ""; // Fallback to an empty string
      const displayName = customer.displayName || ""; // Fallback to an empty string

      return (
        name.toLowerCase().includes(searchValue) ||
        no.toLowerCase().includes(searchValue) ||
        displayName.toLowerCase().includes(searchValue)
      );
    });

    setFilteredCustomers(filtered);
    setShowDropdown(true); // Show the dropdown when the user types
  };

  const handleFocus = () => {
    setSearchTerm(""); // Reset the search term to show all projects
    setFilteredCustomers(customers); // Show all projects when focusing on the input
    setShowDropdown(true); // Show the dropdown
  };

  const handleUpdateProject = async () => {
    setLoading(true);
    const { start_date, end_date, ...updateData } = updatedProject;

    // Validate and format start and end dates
    const validStartDate =
      start_date && !isNaN(Date.parse(start_date))
        ? new Date(start_date).toISOString().split("T")[0]
        : selectedProject.start_date;

    const validEndDate =
      end_date && !isNaN(Date.parse(end_date))
        ? new Date(end_date).toISOString().split("T")[0]
        : selectedProject.end_date;

    // Add the validated/formatted dates to updateData
    updateData.start_date = validStartDate;
    updateData.end_date = validEndDate;

    // Include manager_id in updateData for the API call
    updateData.manager_id = updatedProjectManagers;

    updateData.contact_id = updatedProjectContacts;

    try {
      const response = await api.put(
        `/api/projects/${selectedProject.id}`,
        updateData
      );

      if (response.status === 200) {
        // Find updated manager's name if the manager_id has changed
        let updatedManagerName = selectedProject.manager_name; // Default to current name
        if (updatedProjectManagers !== selectedProject.manager_id) {
          const updatedManager = managers.find(
            (manager) => manager.id === updatedProjectManagers
          );
          updatedManagerName = updatedManager
            ? updatedManager.name
            : updatedManagerName; // Set new manager name if exists
        }
        setTimeout(() => {
          fetchInitialData();
        }, 1000);
        // Update the projects state with new values
        setProjects((prev) =>
          prev.map((proj) =>
            proj.id === selectedProject.id
              ? {
                  ...proj, // Retain other existing fields
                  ...updateData, // Spread updateData to retain updated fields
                  project_manager_names: updatedManagerName, // Update manager name
                }
              : proj
          )
        );
        // window.location.reload();
        handleCloseEditDialog();
      } else {
        toast.error("Failed to update project", {
          position: "bottom-center",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("Failed to update project", {
        position: "bottom-center",
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  //fetching admin users
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/api/users`);
        const activeUsers = res.data.filter(
          (member) => member.status !== "Inactive"
        );
        setUsers(activeUsers);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  //fetching all users and customers
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/users`);
        const cusres = await api.get(`/api/customers`);
        const groupres = await api.get(`/api/groups`);
        const formattedMembers = res.data
          .filter((member) => member.status !== "Inactive")
          .map((member) => ({
            value: member.id,
            label: member.name,
          }))
          .sort((a, b) => {
            return a.label.localeCompare(b.label);
          });
        setMembers(formattedMembers);
        setCustomers(cusres.data);
        setProjectGroup(groupres.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      } finally {
        setLoading(false); // Always stop loading after attempting to fetch
      }
    };

    fetchData();
  }, []);

  // fetching project details
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch both project datasets in parallel
      let projectDataRes, timesheetDataRes;

      if (
        userDetails.role_name === "Administrator" ||
        userDetails.role_name === "Super Admin"
      ) {
        [projectDataRes, timesheetDataRes] = await Promise.all([
          api.get("/api/projectlistdata"),
          api.get("/api/projectlist/timesheets"),
        ]);
      } else {
        [projectDataRes, timesheetDataRes] = await Promise.all([
          api.get(`/api/projectlistdata/${userID}`),
          api.get(`/api/projectlist/timesheets/${userID}`),
        ]);
      }

      let projectData = projectDataRes.data;
      let timesheetData = timesheetDataRes.data;

      // Merge project data with timesheet data by matching project ID
      let mergedProjects = projectData.map((project) => {
        // Find matching timesheet entry for the project
        const matchingTimesheet = timesheetData.find(
          (timesheet) => timesheet.project_id === project.id
        );

        return {
          ...project,
          ...matchingTimesheet, // Merge timesheet fields if found
        };
      });

      // Filter based on active or inactive projects
      let filteredProjects = mergedProjects.filter((project) =>
        activeProjLink === "activeprojects"
          ? project.activestatus === 1
          : project.activestatus === 0
      );

      setProjects(filteredProjects);
      setCurrentPage(1);
    } catch (error) {
      setError(error);
      console.error("Error fetching project data:", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [userDetails.role, userID, activeProjLink]);

  //delete a project
  const handleDeleteConfirm = async () => {
    setLoading(true);
    const idsToDelete = projectToBulkUpdate.map((project) => project.id);

    setDeleteDialogOpen(false); // Close the delete confirmation dialog

    try {
      const response = await api.delete("/api/projects", {
        data: { ids: idsToDelete }, // Send the IDs in the request body
      });

      // Check for successful deletion
      if (response.status === 204) {
        const updatedProjects = projects.filter(
          (project) => !idsToDelete.includes(project.id)
        );
        setBulkUpdate(false);
        setProjects(updatedProjects); // Update the projects state
        setLoading(false);
      } else {
        console.error("Failed to delete projects");
        toast.error("Failed to delete projects. Please try again.", {
          position: "bottom-center",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("There was an error deleting the projects:", error);
      toast.error(
        "An error occurred while deleting projects. Please try again.",
        {
          position: "bottom-center",
          autoClose: 2000,
        }
      );
    } finally {
      setProjectToBulkUpdate([]); // Clear the projects to delete
      handleCloseDial(); // Close any dialog
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]); // Set the selected CSV file
  };

  const handleParse = () => {
    if (!csvFile) return;
    toast.error("Please upload a CSV file", {
      position: "bottom-center",
      autoClose: 2000,
    });
    // Use PapaParse to parse the CSV file
    Papa.parse(csvFile, {
      complete: function (result) {
        setCsvData(result.data); // Parsed CSV data
      },
      header: true, // Treat the first row as headers
    });
  };

  const handleUpload = async () => {
    if (csvData.length === 0) return;
    toast.error("No data to upload", {
      position: "bottom-center",
      autoClose: 2000,
    });

    try {
      // Send CSV data (array of objects) to the backend
      const response = await axios.post("/api/projects/bulk", csvData);
      if (response.status === 200) {
        toast.error("Projects uploaded successfully", {
          position: "bottom-center",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error uploading projects:", error);
      toast.error("Failed to upload projects", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <span className="loading-animation">LOADING...</span>
      </div>
    );
  }

  const mapKeyToProjectProperty = (heading) => {
    switch (
      heading.trim() // Use trim() to remove any accidental spaces
    ) {
      case "Project No":
        return "projectno";
      case "Project Name":
        return "name";
      case "Hour Rate":
        return "hourrate";
      case "Customer Name":
        return "customer_name";
      case "Contact Name":
        return "contact_names";
      case "team":
        return "associated_groups";
      case "Planned Hrs":
        return "overall_budget_hours";
      case "Actual Hrs":
        return "overall_actual_hours";
      case "Difference":
        return "difference_hours";
      case "Tags":
        return "tags";
      case "Manager":
        return "project_manager_names";
      case "Start Date":
        return "start_date";
      case "Due Date":
        return "end_date";
      case "INV %":
        return "average_claimpercent";
      case "Phases":
        return "total_phases";
      case "VO(UC)":
        return "unclaimed_variation_count";
      case "DE AHrs":
        return "design_actual_hours";
      case "DE PHrs":
        return "design_budget_hours";
      case "DO AHrs":
        return "detailing_actual_hours";
      case "DO PHrs":
        return "detailing_budget_hours";
      case "VO PHrs(UC)":
        return "unclaimed_variation_hours";
      case "VO AHrs":
        return "total_variation_actual_hours";
      case "VO PHrs":
        return "total_variation_hours";
      case "S.No":
        return "index";
      case "team":
        return "associated_groups";
      default:
        return "";
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const convertTimeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return 0;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes; // Convert HH:MM to total minutes
  };
  const sortedProjects = [...projects].sort((a, b) => {
    const key = sortConfig?.key || "id"; // Default sorting key is "id"
    const direction = sortConfig?.direction === "asc" ? 1 : -1;
    // Always prioritize sorting by project_status in this order: Completed < Open
    const statusOrder = { Open: 0, Completed: 1 };
    const statusComparison =
      statusOrder[a.project_status] - statusOrder[b.project_status];

    if (statusComparison !== 0) {
      return statusComparison; // Ensures Completed comes before Open
    }

    let aValue = a[key];
    let bValue = b[key];

    // Ensure ID comparison is numeric
    if (key === "id") {
      return (Number(b.id) - Number(a.id)) * direction; // Default: Descending
    }

    switch (key) {
      case "startDate":
      case "dueDate":
        aValue = new Date(aValue);
        bValue = new Date(bValue);
        break;
      case "total_variation_actual_hours":
      case "total_variation_hours":
      case "unclaimed_variation_hourss":
      case "overall_actual_hours":
      case "overall_budget_hours":
      case "difference_hours":
      case "detailing_budget_hours":
      case "detailing_actual_hours":
      case "design_budget_hours":
      case "design_actual_hours":
        aValue = convertTimeToMinutes(aValue);
        bValue = convertTimeToMinutes(bValue);
        break;
      case "project_manager_names":
      case "customer_name":
      case "contact_names":
      case "team":
        aValue = a[key]?.toLowerCase() || "";
        bValue = b[key]?.toLowerCase() || "";
        break;

      default:
        if (typeof aValue === "string") aValue = aValue.toLowerCase();
        if (typeof bValue === "string") bValue = bValue.toLowerCase();
        break;
    }

    if (aValue < bValue) return -1 * direction;
    if (aValue > bValue) return 1 * direction;
    return 0;
  });

  const filteredProjects = sortedProjects.filter((project) => {
    return (
      project.name?.toLowerCase().includes(filter) ||
      project.projectno?.toLowerCase().includes(filter) ||
      project.customer_name?.toLowerCase().includes(filter) ||
      project.tags?.toLowerCase().includes(filter) ||
      project.project_manager_names?.toLowerCase().includes(filter)
    );
  });

  const handleSearch = (e) => {
    setFilter(e.target.value.toLowerCase());
    setFetchData(true);
  };

  const handleDeleteProject = (e, selectedProject) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      setProjectToBulkUpdate((prevSelected) => [
        ...prevSelected,
        selectedProject,
      ]);
    } else {
      setProjectToBulkUpdate((prevSelected) =>
        prevSelected.filter((project) => project.id !== selectedProject.id)
      );
    }
  };

  // Check if all columns are selected (visible)
  const isAllSelected = () => {
    return Object.values(visibleColumns).every((isVisible) => isVisible);
  };

  const handleProjectActiveStatus = async (projectToBulkUpdate, value) => {
    try {
      // Extract only IDs from the projectToBulkUpdate array
      const projectIDs = projectToBulkUpdate.map((project) => project.id);

      await api.put(`/api/bulkupdate-project-status`, {
        projectToBulkUpdate: projectIDs, // Send only an array of IDs
        value,
      });
      setBulkUpdate(false);
      setProjectToBulkUpdate([]);
    } catch (error) {
      toast.error("Failed to update project", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  const exportToExcel = (currentProjects) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Projects");

    // Define the columns based on your row data
    const columns = [
      { header: "Project No", key: "projectNo", width: 20 },
      { header: "Project Name", key: "projectName", width: 30 },
      ...(userDetails.role_name === "Super Admin"
        ? [{ header: "Hour Rate", key: "hourrate", width: 20 }]
        : []),
      { header: "Customer Name", key: "customerName", width: 20 },
      { header: "Contact Name", key: "contactName", width: 20 },
      { header: "VO(UC)", key: "unclaimed_variation_count", width: 15 },
      { header: "VO PHrs(UC)", key: "unclaimed_variation_hours", width: 15 },
      { header: "VO AHrs", key: "total_variation_actual_hours", width: 15 },
      { header: "Planned Hrs", key: "overall_budget_hours", width: 15 },
      { header: "Actual Hrs", key: "overall_actual_hours", width: 15 },
      { header: "Difference", key: "difference_hours", width: 15 },
      { header: "DE PHrs", key: "designBudgetHours", width: 15 },
      { header: "DE AHrs", key: "designActualHours", width: 15 },
      { header: "DO PHrs", key: "detailingBudgetHours", width: 15 },
      { header: "DO AHrs", key: "detailingActualHours", width: 15 },
      { header: "Manager", key: "projectManager", width: 20 },
      { header: "Start Date", key: "startDate", width: 12 },
      { header: "Due Date", key: "dueDate", width: 12 },
      { header: "INV %", key: "invoiceStatus", width: 20 },
      { header: "Description", key: "description", width: 30 },
    ];

    worksheet.columns = columns;

    // Add rows dynamically from the currentProjects data
    currentProjects.forEach((project) => {
      const row = {
        projectNo: project.projectno,
        projectName: project.name,
        hourrate: project.hourrate,
        customerName: project.customer_name,
        contactName: project.contact_names,
        unclaimed_variation_count:
          project.unclaimed_variation_count !== 0
            ? project.unclaimed_variation_count
            : "",
        unclaimed_variation_hours:
          project.unclaimed_variation_hours !== "00:00"
            ? project.unclaimed_variation_hours
            : "",
        total_variation_actual_hours:
          project.total_variation_actual_hours !== "00:00"
            ? project.total_variation_actual_hours
            : "",
        overall_budget_hours:
          project.overall_budget_hours !== "00:00"
            ? project.overall_budget_hours
            : "",
        overall_actual_hours:
          project.overall_actual_hours !== "00:00"
            ? project.overall_actual_hours
            : "",
        difference_hours:
          project.difference_hours !== "00:00" ? project.difference_hours : "",
        designBudgetHours:
          project.design_budget_hours !== "00:00"
            ? project.design_budget_hours
            : "",
        designActualHours:
          project.design_actual_hours !== "00:00"
            ? project.design_actual_hours
            : "",
        detailingBudgetHours:
          project.detailing_budget_hours !== "00:00"
            ? project.detailing_budget_hours
            : "",
        detailingActualHours:
          project.detailing_actual_hours !== "00:00"
            ? project.detailing_actual_hours
            : "",
        projectManager: project.project_manager_names,
        startDate: project.start_date
          ? format(new Date(project.start_date), "dd-MM-yyyy")
          : "",
        dueDate: project.end_date
          ? format(new Date(project.end_date), "dd-MM-yyyy")
          : "",
        invoiceStatus:
          project.average_claimpercent !== 0
            ? `${project.average_claimpercent}% Claimed`
            : "",
        description: project.description,
      };

      worksheet.addRow(row);
    });

    // Apply styles for borders
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Save the file
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "projects.xlsx";
      link.click();
    });
  };

  const convertToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
  };
  const parseTimeToMinutes = (timeString) => {
    if (!timeString || timeString === "00:00") return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const renderRowData = (project) => {
    return columns
      .filter((columnKey) => {
        const role = userDetails.role_name;

        if (columnKey === "Hour Rate") {
          // Only Super Admin can see hourrate
          return role === "Super Admin";
        }

        if (columnKey === "INV %") {
          // Only Admin and Super Admin can see INV %
          return role === "Super Admin" || role === "Administrator";
        }

        // All other columns are visible to everyone
        return true;
      })
      .map((columnKey) => {
        if (visibleColumns[columnKey]) {
          switch (columnKey) {
            case "Project No":
              return (
                <td
                  key="projectNo"
                  style={{
                    backgroundColor:
                      project.project_status === "Completed"
                        ? "#e6fce6"
                        : "White",
                  }}
                >
                  <div
                    className={
                      hasPermission("projects", "update")
                        ? "project-page-table-con"
                        : "project-table-con-user"
                    }
                    style={{ width: "113px" }}
                  >
                    <Link
                      style={{ backgroundColor: "transparent", color: "black" }}
                      to={`/project/${project.id}/dashboard`}
                      className={
                        hasPermission("projects", "update")
                          ? "select-projectlist"
                          : "select-projectlist-user"
                      }
                      onClick={() =>
                        localStorage.setItem("activePLink", "dashboard")
                      }
                    >
                      {project.projectno}
                    </Link>
                    {hasPermission("projects", "update") && (
                      <span
                        className="edit-icon-wrapper"
                        style={{
                          backgroundColor: "transparent",
                          color: "blue",
                        }}
                        title="Edit project"
                      >
                        <EditIcon
                          onClick={() => handleEditClick(project)}
                          style={{
                            backgroundColor: "transparent",
                            color: "blue",
                            fontSize: "1.25rem",
                          }}
                        />
                      </span>
                    )}
                  </div>
                </td>
              );
            case "Project Name":
              return (
                <td
                  key="projectName"
                  style={{
                    backgroundColor:
                      project.project_status === "Completed"
                        ? "#e6fce6"
                        : "White",
                  }}
                >
                  {project.name}
                </td>
              );
            case "Hour Rate":
              return <td key="hourrate">{project?.hourrate}</td>;
            case "Customer Name":
              return (
                <td key="customerName" title={project.customer_name}>
                  {project?.customer_name?.substring(0, 20)}
                </td>
              );
            case "Contact Name":
              return (
                <td key="contactName" title={project.contact_names}>
                  {project?.contact_names?.substring(0, 20)}
                </td>
              );
            case "Team":
              return <td key="team">{project.associated_groups}</td>;
            case "VO(UC)":
              return (
                <td
                  key="unclaimed_variation_count"
                  style={{ textAlign: "center" }}
                >
                  {project.unclaimed_variation_count === 0
                    ? ""
                    : project.unclaimed_variation_count}
                </td>
              );
            case "VO PHrs(UC)":
              return (
                <td
                  key="unclaimed_variation_hours"
                  style={{ textAlign: "center" }}
                >
                  {project.unclaimed_variation_hours === "00:00"
                    ? ""
                    : project.unclaimed_variation_hours}
                </td>
              );
            case "VO PHrs":
              return (
                <td key="total_variation_hours" style={{ textAlign: "center" }}>
                  {project.total_variation_hours &&
                  project.total_variation_hours !== "00:00"
                    ? project.total_variation_hours
                    : ""}
                </td>
              );
            case "VO AHrs":
              return (
                <td
                  key="total_variation_actual_hours"
                  style={{ textAlign: "center" }}
                >
                  {project.total_variation_actual_hours &&
                  project.total_variation_actual_hours !== "00:00"
                    ? project.total_variation_actual_hours
                    : ""}
                </td>
              );

            case "Planned Hrs":
              return (
                <td key="overall_budget_hours" style={{ textAlign: "center" }}>
                  {project.overall_budget_hours === "00:00"
                    ? ""
                    : project.overall_budget_hours}
                </td>
              );
            case "Actual Hrs":
              const budgetHours = parseTimeToMinutes(
                project.overall_budget_hours
              );
              const actualHours = parseTimeToMinutes(
                project.overall_actual_hours
              );

              let textColor = "black"; // Default color

              if (budgetHours > 0) {
                const percentage = (actualHours / budgetHours) * 100;
                if (percentage === 100) {
                  textColor = "green";
                } else if (percentage > 99) {
                  textColor = "red";
                } else if (percentage >= 50) {
                  textColor = "#ffa500";
                }
              }
              return (
                <td
                  key="overall_actual_hours"
                  style={{
                    textAlign: "center",
                    color: textColor,
                  }}
                >
                  {project.overall_actual_hours === "00:00"
                    ? ""
                    : project.overall_actual_hours}
                </td>
              );
            case "Difference":
              return (
                <td
                  key={"difference_hours"}
                  style={{
                    textAlign: "center",
                    color:
                      convertToMinutes(project.overall_actual_hours) >
                      convertToMinutes(project.overall_budget_hours)
                        ? "red"
                        : "green",
                  }}
                >
                  {project.difference_hours !== "00:00"
                    ? project.difference_hours
                    : ""}
                </td>
              );
            case "DE PHrs":
              return (
                <td key="designBudgetHours" style={{ textAlign: "center" }}>
                  {project.design_budget_hours === "00:00"
                    ? ""
                    : project.design_budget_hours}
                </td>
              );
            case "DE AHrs":
              return (
                <td key="designActualHours" style={{ textAlign: "center" }}>
                  {project.design_actual_hours === "00:00"
                    ? ""
                    : project.design_actual_hours}
                </td>
              );
            case "DO PHrs":
              return (
                <td key="detailingBudgetHours" style={{ textAlign: "center" }}>
                  {project.detailing_budget_hours === "00:00"
                    ? ""
                    : project.detailing_budget_hours}
                </td>
              );
            case "DO AHrs":
              return (
                <td key="detailingActualHours" style={{ textAlign: "center" }}>
                  {project.detailing_actual_hours === "00:00"
                    ? ""
                    : project.detailing_actual_hours}
                </td>
              );
            case "Manager":
              return (
                <td key="projectManager">{project.project_manager_names}</td>
              );
            case "Phases":
              return (
                <td
                  key="phases"
                  style={{
                    textAlign: "center",
                    position: "relative",
                    cursor: "pointer",
                  }}
                  title={project.phase_statuses}
                >
                  <Link
                    to={`/project/phases/${project.id}`}
                    className="select-projectlist-phase"
                    style={{ marginRight: "0px" }}
                  >
                    {project.total_phases !== 0 ? project.total_phases : ""}
                  </Link>
                </td>
              );
            case "Start Date":
              return (
                <td key="startDate">
                  {format(project?.start_date, "dd-MM-yyyy")}
                </td>
              );
            case "Due Date":
              return (
                <td key="dueDate">{format(project?.end_date, "dd-MM-yyyy")}</td>
              );
            case "INV %":
              return (
                <td key="invoiceStatus" style={{ textAlign: "center" }}>
                  <span
                    style={{
                      backgroundColor: "transparent",
                      textAlign: "center",
                    }}
                  >
                    {Number(project.average_claimpercent) === 0
                      ? ""
                      : `${project.average_claimpercent} %`}
                  </span>
                </td>
              );
            default:
              return null;
          }
        }
        return null;
      });
  };
  const updateDetails = async (updatedProjectGroup) => {
    try {
      const response = await api.put(
        `/api/projects/${selectedProject.id}/update-groups`,
        { groups: updatedProjectGroup } // Send selected options as groups
      );
      if (!response.ok) {
        throw new Error("Failed to update groups");
      }
    } catch (error) {
      console.error("Error updating groups:", error);
    }
  };
  const handleBlur = async () => {
    try {
      // Call the API to update the selected groups using the PUT method
      await updateDetails(updatedProjectGroup);
    } catch (error) {
      console.error("Error updating details:", error.message);
    }
  };
  const groupProjects = (projects, groupBy) => {
    if (
      typeof groupBy !== "string" ||
      groupBy.trim() === "" ||
      groupBy === "All Projects"
    )
      return null;

    const grouped = {};

    projects.forEach((p) => {
      let key = "";
      switch (groupBy) {
        case "Customer":
          key = p.customer_name || "Unknown";
          break;
        case "Team":
          key = p.associated_groups?.[0] || "Unknown";
          break;
        case "Manager":
          key = p.project_manager_names || "Unknown";
          break;
        case "Completed":
          if (p.project_status !== "Completed") return; // skip non-completed
          key = "Completed";
          break;
        case "Active":
          if (p.project_status === "Completed" || p.activestatus === 0) return; // skip non-completed
          key = "Active";
          break;
        case "Archived":
          if (p.project_status === "Completed" || p.activestatus === 1) return; // skip non-completed
          key = "Archived";
          break;
        case "UnclaimedVo":
          if (p.unclaimed_variation_count === 0) return;
          key = "UnclaimedVo";
          break;
        case "UnacceptedVo":
          if (p.unaccepted_variation_count === 0) return;
          key = "UnacceptedVo";
          break;
        case "VO to Confirm":
          if (p.confirmed_variation_count === 0) return;
          key = "VO to Confirm";
          break;
        case "Unapproved VO":
          if (p.unapproved_variation_count === 0) return;
          key = "Unapproved VO";
          break;
        case "Open RFIs":
          if (p.open_rfi_count === 0) return;
          key = "Open RFIs";
          break;
        case "Outstanding RFIs":
          if (p.outstanding_rfi_count === 0) return;
          key = "Outstanding RFIs";
          break;
      }

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(p);
    });

    // Return sorted entries
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  };

  const allGroupedProjects =
    groupBy !== "All Projects"
      ? groupProjects(filteredProjects, groupBy)
      : null;

  // Flatten grouped projects if grouping is applied
  const flattenedProjects = allGroupedProjects
    ? Object.values(allGroupedProjects).flat()
    : filteredProjects;

  // Apply pagination on flattened list
  const totalPages = Math.ceil(flattenedProjects.length / projectsPerPage);
  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = flattenedProjects.slice(
    indexOfFirstProject,
    indexOfLastProject
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };
  const handleFirstPage = () => {
    if (currentPage > 1) setCurrentPage(1);
  };
  const handleLastPage = () => {
    if (currentPage < totalPages) setCurrentPage(totalPages);
  };

  const groupedProjects =
    groupBy !== "All Projects" && groupProjects(currentProjects, groupBy);

  return (
    <div
      className={`${
        userDetails.role_name === "Administrator" ||
        userDetails.role_name === "Super Admin"
          ? "project-admin-con"
          : "project-user-con"
      } ${isNavBarOpen ? "navbar-open" : "navbar-closed"}`}
    >
      <Helmet>
        <title>Msuite - Projects</title>
      </Helmet>
      <ToastContainer />

      <div
        className={
          isNavBarOpen
            ? "projectlist-nav-bar-details"
            : "projectlist-nav-bar-details-close"
        }
      >
        <div className="project-tab-details">
          {hasPermission("workinprogress", "read") && (
            <Link
              className={`proj-links ${
                activeProjLink === "workinprogress" ? "active" : ""
              }`}
              onClick={() => handleProjLinkClick("workinprogress")}
            >
              Work in Progress
            </Link>
          )}
          <Link
            className={`proj-links ${
              activeProjLink === "activeprojects" ? "active" : ""
            }`}
            onClick={() => handleProjLinkClick("activeprojects")}
          >
            Active Projects
          </Link>
          {hasPermission("projects", "update") && (
            <Link
              className={`proj-links ${
                activeProjLink === "archivedprojects" ? "active" : ""
              }`}
              onClick={() => handleProjLinkClick("archivedprojects")}
            >
              Archived Projects
            </Link>
          )}
        </div>
        <hr className="proj-line" />
      </div>

      {hasPermission("projects", "create") && (
        <>
          {activeProjLink !== "workinprogress" && (
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() =>
                exportToExcel(
                  projectToBulkUpdate.length > 0
                    ? projectToBulkUpdate
                    : flattenedProjects
                )
              }
              className={
                isNavBarOpen ? "add-exp-heading" : "add-exp-heading-close"
              }
            >
              Export to Excel
            </motion.button>
          )}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className={
              isNavBarOpen ? "add-proj-heading" : "add-proj-heading-close"
            }
            onClick={onHandleAddProject}
            title="New project"
          >
            New Project
          </motion.button>
        </>
      )}
      {hasPermission("projects", "delete") &&
        projectToBulkUpdate.length > 0 &&
        activeProjLink !== "workinprogress" && (
          <motion.button
            className={
              isNavBarOpen ? "delete-proj-heading" : "delete-proj-heading-close"
            }
            onClick={() => setBulkUpdate((prev) => !prev)}
          >
            Bulk Update
          </motion.button>
        )}
      {bulkUpdate && (
        <div className="project-bulk-update-container" ref={bulkUpdateRef}>
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="delete-proj-btn"
            onClick={() => setDeleteDialogOpen(true)}
            style={{
              backgroundColor: "red",
            }}
            title="Delete project"
          >
            Delete
          </motion.button>
          <select
            onChange={(e) =>
              handleProjectActiveStatus(projectToBulkUpdate, e.target.value)
            }
            className="project-active-status"
            disabled={!hasPermission("projects", "update")}
          >
            <option>Change Status</option>
            {activeProjLink === "archivedprojects" && (
              <option value={"1"}>Active</option>
            )}
            {activeProjLink === "activeprojects" && (
              <option value={"0"}>Archived</option>
            )}
          </select>
        </div>
      )}
      {hasPermission("projects", "create") && (
        <div className="upload-project-container">
          <input type="file" accept=".csv" onChange={handleFileChange} />
          <button onClick={handleParse}>Parse CSV</button>
          <a href={sampleCSV} download="sample_projects.csv">
            <button>Download Sample CSV</button>
          </a>
          {csvData.length > 0 && (
            <div>
              <h3>Parsed Projects</h3>
              <pre>{JSON.stringify(csvData, null, 2)}</pre>
              <button onClick={handleUpload}>Upload Projects</button>
            </div>
          )}
        </div>
      )}
      <input
        type="search"
        className={isNavBarOpen ? "project-search" : "project-search-close"}
        placeholder="Search by project name or number or customer name"
        value={filter}
        onChange={handleSearch}
        onBlur={() => setFetchData(true)}
        title="Search project details"
        ref={fetchDataRef}
      />
      <select
        className={
          hasPermission("projects", "create")
            ? "project-group-by"
            : "project-group-by-1"
        }
        value={groupBy}
        onChange={(e) => {
          setGroupBy(e.target.value);
          setCurrentPage(1);
        }}
      >
        <option value="All Projects">All Projects</option>
        <option value="Customer">Customer</option>
        <option value="Team">Team</option>
        <option value="Manager">Manager</option>
        <option value="Active">Active</option>
        <option value="Archived">Archived</option>
        <option value="Completed">Completed</option>{" "}
        <option value="UnclaimedVo">Unclaimed VO</option>
        <option value="UnacceptedVo">Unaccepted VO</option>
        <option value="VO to Confirm">Unconfirmed VO</option>
        <option value="Unapproved VO">Unapproved VO</option>
        <option value="Open RFIs">Open RFIs</option>
        <option value="Outstanding RFIs">Outstanding RFIs</option>
      </select>
      {/* )} */}
      <div
        className={
          isNavBarOpen ? "project-container" : "project-container-close"
        }
      >
        <div>
          {isFormVisible && (
            <form className="project-column-hide">
              <div className="project-column-hide-container">
                <div className="project-col-swap-header">
                  <h3
                    className="add-project-user-heading"
                    style={{ marginLeft: "14px" }}
                  >
                    Add, Remove, or Swap Columns
                  </h3>
                </div>
                <IoMdClose
                  onClick={toggleFormVisibility}
                  className="close-column-icon"
                />
                <div
                  style={{ marginTop: "45px" }}
                  className="project-col-header-container"
                >
                  <label className="project-column-swap">
                    <input
                      type="checkbox"
                      onChange={() => {
                        const newState = !isAllSelected();

                        const updatedColumns = Object.keys(
                          visibleColumns
                        ).reduce((acc, key) => {
                          // Always keep "Project No" visible
                          acc[key] = key === "Project No" ? true : newState;
                          return acc;
                        }, {});

                        setVisibleColumns(updatedColumns);
                        saveUserPreferences(updatedColumns);
                      }}
                      checked={isAllSelected()}
                      style={{ marginRight: "20px", cursor: "pointer" }}
                    />
                    Select All
                  </label>
                </div>
                {Object.keys(visibleColumns).map((heading, index) => (
                  <div
                    key={heading}
                    className="project-col-header-container"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <label
                      className="project-column-swap"
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDrop={() => handleDrop(index)}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[heading]}
                        onChange={() => toggleColumnVisibility(heading)}
                        style={{ marginRight: "20px", cursor: "pointer" }}
                      />
                      {heading}
                    </label>

                    <span
                      onClick={() => toggleStickyColumn(heading)}
                      style={{ cursor: "pointer", marginLeft: "10px" }}
                    >
                      {stickyColumns.includes(heading) ? (
                        <TiPin />
                      ) : (
                        <TiPinOutline />
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </form>
          )}
        </div>
        {activeProjLink !== "workinprogress" ? (
          <div
            className={
              isNavBarOpen
                ? "project-table-wrapper"
                : "project-table-wrapper-close"
            }
          >
            <table
              className={
                isNavBarOpen ? "project-list-table" : "project-list-table-close"
              }
              ref={tableRef}
              style={{ top: "-16px" }}
            >
              <thead
                className="sticky-header"
                style={{ position: "sticky", top: "-1px", zIndex: "2" }}
              >
                <tr>
                  <th>
                    <FaChevronDown />
                  </th>

                  {Object.keys(visibleColumns)
                    .filter((heading) => {
                      const role = userDetails.role_name;

                      if (heading === "Hour Rate") {
                        // Only Super Admin can see hourrate
                        return role === "Super Admin";
                      }

                      if (heading === "INV %") {
                        // Only Admin and Super Admin can see INV %
                        return (
                          role === "Super Admin" || role === "Administrator"
                        );
                      }

                      // All other columns are visible to everyone
                      return true;
                    })
                    .map((heading, index) =>
                      visibleColumns[heading] ? (
                        <th
                          key={heading}
                          style={{
                            width:
                              heading === "Project No"
                                ? "125px"
                                : columnWidths[index],
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            if (!isResizing.current) {
                              handleSort(mapKeyToProjectProperty(heading));
                            }
                          }}
                          onContextMenu={(e) => handleRightClick(e, heading)}
                        >
                          <div
                            className="resize-handle"
                            onMouseDown={(e) => handleMouseDown(index, e)}
                          />
                          {heading}
                          {sortConfig.key ===
                            mapKeyToProjectProperty(heading) && (
                            <span>
                              {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                            </span>
                          )}
                        </th>
                      ) : null
                    )}
                  <th
                    style={{
                      position: "sticky",
                      right: "-2px",
                      backgroundColor: "white",
                      borderCollapse: "collapse",
                      textAlign: "center",
                      padding: "5px",
                      zIndex: 2, // Ensure it appears on top
                      width: "20px",
                      borderLeft: "1px solid #9d9d9d",
                    }}
                  >
                    <PiColumnsPlusRight
                      className="column-icon"
                      onClick={toggleFormVisibility}
                      title="Add Column"
                      style={{
                        cursor: "pointer",
                        fontSize: "20px",
                        color: "#808080",
                      }} // Optional styling for icon
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {groupBy && groupedProjects
                  ? Object.entries(groupedProjects).map(([group, projects]) => (
                      <React.Fragment key={group}>
                        {/* Group Header */}
                        <tr
                          style={{
                            backgroundColor: "white",
                            fontWeight: "bold",
                            border: "1px solid #9d9d9d",
                            position: "sticky", // Make the entire row sticky
                            left: 0, // Stick to the left side
                            zIndex: 10,
                          }}
                        >
                          <td
                            colSpan={19}
                            style={{
                              textAlign: "left",
                              padding: "10px",
                              position: "sticky", // Make this cell sticky as well (redundant if whole row sticky)
                              left: 0,
                              backgroundColor: "white", // Ensure background covers other content
                              zIndex: 12,
                            }}
                          >
                            {group}
                          </td>
                        </tr>

                        {/* Grouped Project Rows */}
                        {projects.map((project) => (
                          <tr
                            key={project.id}
                            style={{
                              border: "none",
                              color: "black",
                              fontWeight: "400",
                              backgroundColor:
                                project.project_status === "Completed"
                                  ? "#e6fce6"
                                  : "White",
                            }}
                          >
                            <td
                              style={{
                                backgroundColor:
                                  project.project_status === "Completed"
                                    ? "#e6fce6"
                                    : "White",
                              }}
                            >
                              <input
                                disabled={!hasPermission("projects", "delete")}
                                type="checkbox"
                                style={{
                                  cursor: hasPermission("projects", "delete")
                                    ? "pointer"
                                    : "not-allowed",
                                }}
                                name={project.name}
                                checked={projectToBulkUpdate.some(
                                  (p) => p.id === project.id
                                )}
                                onChange={(e) =>
                                  handleDeleteProject(e, project)
                                }
                              />
                            </td>
                            {renderRowData(project)}
                            <td></td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))
                  : currentProjects.map((project) => (
                      <tr
                        key={project.id}
                        style={{
                          color: "black",
                          fontWeight: "400",
                          backgroundColor:
                            project.project_status === "Completed"
                              ? "#e6fce6"
                              : "White",
                        }}
                      >
                        <td
                          style={{
                            backgroundColor:
                              project.project_status === "Completed"
                                ? "#e6fce6"
                                : "White",
                          }}
                        >
                          <input
                            disabled={!hasPermission("projects", "delete")}
                            type="checkbox"
                            style={{
                              cursor: hasPermission("projects", "delete")
                                ? "pointer"
                                : "not-allowed",
                            }}
                            name={project.name}
                            checked={projectToBulkUpdate.some(
                              (p) => p.id === project.id
                            )}
                            onChange={(e) => handleDeleteProject(e, project)}
                          />
                        </td>
                        {renderRowData(project)}
                        <td></td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        ) : (
          <WorkingProgressSheet
            searchFilter={filter}
            fetchData={fetchData}
            groupBy={groupBy}
          />
        )}
        {contextMenu.visible && (
          <div
            style={{
              position: "absolute",
              top: "67px",
              left: contextMenu.x,
              backgroundColor: "white",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              padding: "8px",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              width: "130px",
            }}
            ref={projectRef}
          >
            <span className="hide-column-span" title="hide-column">
              <GrHide className="hide-icon" />
              <motion.button
                onClick={hideColumn}
                className="hide-column"
                whileTap={{ scale: 0.9 }}
              >
                Hide Column
              </motion.button>
            </span>
          </div>
        )}
      </div>
      {!isFormVisible && activeProjLink !== "workinprogress" && (
        <div
          className={
            userDetails.role_name === "Administrator" ||
            userDetails.role_name === "Project_Manager" ||
            userDetails.role_name === "Super Admin"
              ? "pagination-controls"
              : "pagination-controls-user"
          }
        >
          <h3 style={{ fontWeight: "normal", margin: "0", fontSize: "14px" }}>
            Total : {flattenedProjects.length}
          </h3>
          <select
            className="project-list-row-count"
            value={projectsPerPage}
            onChange={(e) => {
              setProjectsPerPage(Number(e.target.value));
            }}
          >
            <option value="25">25 Records Per Page</option>
            <option value="50">50 Records Per Page</option>
            <option value="75">75 Records Per Page</option>
            <option value="100">100 Records Per Page</option>
          </select>
          <button
            onClick={handleFirstPage}
            disabled={currentPage === 1}
            className="arrow-project-btn"
          >
            <MdKeyboardDoubleArrowLeft title="First Page" />
          </button>
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="arrow-project-btn"
          >
            {/* */}
            <MdKeyboardArrowLeft title="Previous Page" />
          </button>
          <span
            style={{
              textWrap: "nowrap",
            }}
          >
            {paginationCount}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="arrow-project-btn"
          >
            <MdKeyboardArrowRight title="Next Page" />
          </button>
          <button
            onClick={handleLastPage}
            disabled={currentPage === totalPages}
            className="arrow-project-btn"
          >
            <MdKeyboardDoubleArrowRight title="Last Page" />
          </button>
        </div>
      )}
      {openEditDialog && (
        <div className="edit-proj-dial">
          <div class="dialog-edit-content">
            <div className="edit-project-user-header1">
              <h3 class="dialog-title">Edit Project</h3>
            </div>
            <div className="edit-project-con">
              <h3 className="edit-proj-label">Project Name</h3>
              <input
                type="text"
                name="name"
                placeholder="Project Name"
                value={updatedProject?.name || ""}
                onChange={handleInputChange}
                className="edit-input"
              />
              <h3 className="edit-proj-label">Project No</h3>
              <input
                type="text"
                name="projectno"
                placeholder="Project No"
                value={updatedProject?.projectno || ""}
                onChange={handleInputChange}
                className="edit-input"
              />
              {userDetails.role_name === "Super Admin" && (
                <div>
                  {" "}
                  <h3 className="edit-proj-label">Hour Rate</h3>
                  <input
                    type="text"
                    name="hourrate"
                    placeholder="Hour Rate"
                    value={updatedProject?.hourrate || ""}
                    onChange={handleInputChange}
                    className="edit-input"
                  />
                </div>
              )}
              <h3 className="edit-proj-label">Project Owner</h3>
              <MultiSelect
                className="pm-dropdown"
                value={
                  Array.isArray(updatedProjectManagers)
                    ? updatedProjectManagers
                    : [updatedProjectManagers]
                }
                name="project_managers"
                options={users
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((user) => ({
                    label: user.name,
                    value: String(user.id),
                  }))}
                onChange={(e) => setUpdatedProjectManagers(e)}
              />
              <h3 className="edit-proj-label">Project group</h3>
              <MultiSelect
                className="pm-dropdown"
                value={
                  Array.isArray(updatedProjectGroup)
                    ? updatedProjectGroup
                    : [updatedProjectGroup]
                }
                name="project_managers"
                options={projectGroup
                  .sort((a, b) => a.code.localeCompare(b.code))
                  .map((g) => ({
                    label: g.code,
                    value: String(g.id),
                  }))}
                onBlur={handleBlur}
                onChange={(e) => setUpdatedProjectGroup(e)}
              />
              <h3 className="edit-proj-label">Customer Name</h3>
              <select
                style={{ cursor: "pointer" }}
                name="customer_id"
                className="edit-input-dropdown"
                value={updatedProject.customer_id}
                onChange={handleInputChange}
              >
                <option>Select Customer</option>
                {customers
                  .sort((a, b) => {
                    const aCustomerno = a.customerno || "";
                    const bCustomerno = b.customerno || "";
                    return aCustomerno.localeCompare(bCustomerno);
                  })
                  .map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.customerno}-{customer.name}
                    </option>
                  ))}
              </select>
              <h3 className="edit-proj-label">Contact </h3>
              <MultiSelect
                className="pm-dropdown"
                value={
                  Array.isArray(updatedProjectContacts)
                    ? updatedProjectContacts
                    : [updatedProjectContacts]
                }
                name="project_managers"
                options={contacts.map((contact) => ({
                  label: contact.contact_name,
                  value: String(contact.contact_id),
                }))}
                onChange={(e) => setUpdatedProjectContacts(e)}
              />
              <h3 className="edit-proj-label">Start Date</h3>
              <DatePicker
                placeholderText="dd-mm-yyyy"
                selected={
                  updatedProject.start_date
                    ? new Date(updatedProject.start_date)
                    : null
                }
                onChange={(date) => handleDateChange(date, "start_date")}
                dateFormat="dd-MM-yyyy"
                className="edit-date-input"
                maxDate={
                  updatedProject.end_date
                    ? new Date(updatedProject.end_date)
                    : null
                }
              />
              <h3 className="edit-proj-label">End Date</h3>
              <DatePicker
                placeholderText="dd-mm-yyyy"
                selected={
                  updatedProject.end_date
                    ? new Date(updatedProject.end_date)
                    : null
                }
                onChange={(date) => handleDateChange(date, "end_date")}
                dateFormat="dd-MM-yyyy"
                className="edit-date-input"
                minDate={
                  updatedProject.start_date
                    ? new Date(updatedProject.start_date)
                    : null
                }
              />
              <h3 className="edit-proj-label">Description</h3>
              <textarea
                className="edit-description"
                name="description"
                placeholder="Description"
                value={updatedProject?.description || ""}
                onChange={handleInputChange}
              />
              {updatedProjectManagers.length === 0 && (
                <p style={{ color: "Red", fontSize: "12px" }}>
                  Please select a manager to update
                </p>
              )}
            </div>
            <div class="edit-proj-btn-con">
              <button
                onClick={() => {
                  handleUpdateProject();
                  handleBlur();
                }}
                disabled={updatedProjectManagers.length === 0}
                style={{
                  marginLeft: "23px",
                  backgroundColor:
                    updatedProjectManagers.length === 0 ? "grey" : "#6200ee",
                }}
                className="add-btn"
              >
                Save
              </button>
              <button
                onClick={() => setOpenEditDialog(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {openDialog && (
        <>
          <div className="add-project-dial">
            <div className="add-new-project-content">
              <div className="add-project-user-header">
                <h3
                  className="add-project-user-heading"
                  style={{ marginLeft: "14px" }}
                >
                  New Project
                </h3>
              </div>
              <div className="project-name-number-container">
                <div className="name-container">
                  <h3 className="box-heading">Project No</h3>
                  <input
                    className="box-input"
                    placeholder="Enter Project No"
                    maxLength={20}
                    value={newProject.projectno}
                    onChange={(e) =>
                      setNewProject((prev) => ({
                        ...prev,
                        projectno: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="name-container" style={{ marginLeft: "14px" }}>
                  <h3 className="box-heading">Project Name</h3>
                  <input
                    className="box-input"
                    placeholder="Enter Project Name"
                    value={newProject.name}
                    maxLength={50}
                    onChange={(e) =>
                      setNewProject((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="start-date-due-date-container">
                <div className="start-dat-container">
                  <h3 className="box-heading">Start date</h3>
                  <DatePicker
                    placeholderText="dd-mm-yyyy"
                    selected={newProject.start_date}
                    onChange={(date) =>
                      setNewProject((prev) => ({
                        ...prev,
                        start_date: date,
                      }))
                    }
                    dateFormat="dd-MM-yyyy"
                    className="box-input"
                    maxDate={newProject.end_date}
                  />
                </div>
                <div className="due-date-container">
                  <h3 className="box-heading">Due date</h3>
                  <DatePicker
                    placeholderText="dd-mm-yyyy"
                    selected={newProject.end_date}
                    onChange={(date) =>
                      setNewProject((prev) => ({
                        ...prev,
                        end_date: date,
                      }))
                    }
                    dateFormat="dd-MM-yyyy"
                    className="box-input"
                    minDate={newProject.start_date}
                  />
                </div>
              </div>
              <div className="priority-container"></div>
              <div className="project-owner-budget-container">
                <div style={{ backgroundColor: "transparent" }}>
                  <h3 className="box-heading">Select Customer</h3>
                  <input
                    className="box-input"
                    placeholder="Search Customer"
                    ref={dropdownRef}
                    value={searchTerm || selectedCustomerName}
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                    onMouseDown={() => handleClickOutside}
                  />
                  {showDropdown &&
                    (showDropdown && filteredCustomers?.length > 0 ? (
                      <ul
                        style={{ zIndex: "50" }}
                        ref={dropdownRef}
                        className="project-p-display-box"
                      >
                        {filteredCustomers.map((customer) => (
                          <li
                            key={customer.id}
                            value={customer.id}
                            onClick={() => {
                              setNewProject((prev) => ({
                                ...prev,
                                customer_id: customer.id,
                              }));
                              setShowDropdown(false);
                              setSearchTerm("");
                            }}
                            onMouseDown={() => handleClickOutside}
                            className="project-dropdown-item"
                          >
                            {customer.customerno}-{customer.name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No Customers available</p>
                    ))}
                </div>
                <div className="name-container" style={{ marginLeft: "14px" }}>
                  <h3 className="box-heading">Project Group</h3>
                  <select
                    className="select-d-input"
                    style={{
                      marginTop: "0px",
                      backgroundColor: "white",
                      height: "30px",
                    }}
                    value={newProject.team_id}
                    onChange={(e) => {
                      const selectedGroupId = e.target.value;
                      setNewProject((prev) => ({
                        ...prev,
                        team_id: selectedGroupId,
                      }));
                    }}
                  >
                    <option value="" disabled>
                      Select Group
                    </option>
                    {projectGroup
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.code}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="project-select-container">
                <div className="project-new-project-container "></div>
              </div>
              <div className="owner-container">
                <h3 className="box-heading" style={{ margin: "3px" }}>
                  Contact
                </h3>
                <MultiSelect
                  value={newProject.contact_id} // ⬅️ Always pass the selected value here
                  options={contacts.map((contact) => ({
                    label: contact.contact_name,
                    value: String(contact.contact_id),
                  }))}
                  className="member-select-option"
                  onChange={(e) => {
                    setNewProject((prev) => ({
                      ...prev,
                      contact_id: e,
                    }));
                  }}
                  display="chip"
                  placeholder="Select Project Managers"
                />
              </div>
              <div className="owner-container">
                <h3 className="box-heading" style={{ margin: "3px" }}>
                  Owner
                </h3>
                <MultiSelect
                  value={newProject.manager_id} // ⬅️ Always pass the selected value here
                  options={users
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((user) => ({
                      label: user.name,
                      value: String(user.id),
                    }))}
                  className="member-select-option"
                  onChange={(e) => {
                    setNewProject((prev) => ({
                      ...prev,
                      manager_id: e,
                    }));
                  }}
                  display="chip"
                  placeholder="Select Project Managers"
                />
              </div>
              <div className="project-details-container">
                <h3 className="box-heading">Project Members</h3>
                <MultiSelect
                  options={members}
                  value={selectedMembers}
                  onChange={setSelectedMembers}
                  className="member-select-option"
                />
              </div>
              <div className="add-description-container ">
                <div style={{ backgroundColor: "transparent" }}>
                  <h3 className="box-heading">Add Description</h3>
                  <textarea
                    className="box-des-input"
                    placeholder="Enter Description"
                    value={newProject.description}
                    maxLength={100}
                    onChange={(e) =>
                      setNewProject((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>
                <div style={{ display: "flex" }}>
                  <div className="tags-container"></div>
                </div>
              </div>
              <div className="add-new-project-button-container">
                <motion.button
                  className="add-btn"
                  onClick={handleAddNewProject}
                  whileTap={{ scale: 0.9 }}
                >
                  Add Project
                </motion.button>

                <motion.button
                  onClick={closeDialog}
                  className="cancel-btn"
                  whileTap={{ scale: 0.9 }}
                >
                  Cancel
                </motion.button>
              </div>
            </div>
          </div>
        </>
      )}
      {deleteDialogOpen && (
        <div className="delete-phase-dial">
          <div className="delete-dial">
            <div className="delete-header">
              <h3
                style={{
                  backgroundColor: "transparent",
                  marginLeft: "15px",
                  fontSize: "15px",
                }}
              >
                Delete Project
              </h3>
            </div>
            <div style={{ backgroundColor: "white" }}>
              <p style={{ backgroundColor: "white" }}>
                Are you sure you want to delete this Project? This action cannot
                be undone.
              </p>
              <input
                style={{ cursor: "pointer" }}
                type="checkbox"
                id="delete-phase"
                checked={isCheckboxChecked}
                onChange={() => setIsCheckboxChecked((prev) => !prev)}
              />
              <label
                htmlFor="delete-phase"
                style={{ backgroundColor: "white", cursor: "pointer" }}
              >
                I understand this action cannot be undone.
              </label>
            </div>
            <div className="delete-dial-btn-con">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={handleDeleteConfirm}
                color="secondary"
                variant="contained"
                disabled={!isCheckboxChecked}
                className="add-btn"
                style={{
                  backgroundColor: isCheckboxChecked ? "red" : "gray",
                }}
              >
                Delete
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={handleCloseDial}
                className="cancel-btn"
              >
                Cancel
              </motion.button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectList;
